import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useChangePassword, useMe } from '../hooks';
import { useIntl } from '../hooks/useIntl.js';
import { zodResolver } from '../lib/zod-resolver.js';
import { type ChangePasswordForm, changePasswordSchema } from '../validations/change-password';

export default function Profile() {
  const intl = useIntl();
  const { data: me } = useMe();
  const { mutate: changePassword, isPending, error } = useChangePassword();
  const [passwordSuccessMessage, setPasswordSuccessMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = (values: ChangePasswordForm) => {
    setPasswordSuccessMessage(null);
    changePassword(
      {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
      {
        onSuccess: () => {
          setPasswordSuccessMessage(
            intl.formatMessage({ id: 'profile.changePassword.successMessage' }),
          );
          reset();
        },
      },
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="bg-background-light shadow rounded-md p-6 space-y-2">
        <h1 className="text-lg font-semibold">{intl.formatMessage({ id: 'profile.title' })}</h1>
        <div className="text-sm text-foreground">
          <div>
            <span className="font-medium">{intl.formatMessage({ id: 'profile.username' })}:</span>{' '}
            {me?.username ?? '—'}
          </div>
          <div>
            <span className="font-medium">{intl.formatMessage({ id: 'profile.role' })}:</span>{' '}
            {me?.role ?? '—'}
          </div>
        </div>
      </div>

      <div className="bg-background-light shadow rounded-md p-6">
        <h2 className="text-base font-semibold mb-4">
          {intl.formatMessage({ id: 'profile.changePassword.title' })}
        </h2>
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-1">
            <Label htmlFor="currentPassword">
              {intl.formatMessage({ id: 'profile.changePassword.currentPassword' })}
            </Label>
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              {...register('currentPassword')}
            />
            {errors.currentPassword && (
              <p className="text-sm text-destructive">{errors.currentPassword.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="newPassword">
              {intl.formatMessage({ id: 'profile.changePassword.newPassword' })}
            </Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              {...register('newPassword')}
            />
            {errors.newPassword && (
              <p className="text-sm text-destructive">{errors.newPassword.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="confirmPassword">
              {intl.formatMessage({ id: 'profile.changePassword.confirmPassword' })}
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          {error && (
            <div className="text-sm text-destructive">
              {error instanceof Error
                ? error.message
                : intl.formatMessage({ id: 'profile.changePassword.errorMessage' })}
            </div>
          )}

          {passwordSuccessMessage && (
            <div className="text-sm text-green-600">{passwordSuccessMessage}</div>
          )}

          <Button type="submit" disabled={isPending}>
            {isPending
              ? intl.formatMessage({ id: 'profile.changePassword.updating' })
              : intl.formatMessage({ id: 'profile.changePassword.updateButton' })}
          </Button>
        </form>
      </div>
    </div>
  );
}
